import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";

interface DealReportsState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date[];
    deliveryZone: any;
    clients: any;
  };

  items: any[];
}

const state: DealReportsState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    deliveryZone: null,
    clients: null,
    targetDates: [new Date(), new Date()],
  },

  items: [],
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates[0]).format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates[1]).format("YYYY-MM-DD");
  }

  if (params.dZoneGroups) {
    params.dZoneGroups = Array.isArray(params.dZoneGroups)
      ? params.dZoneGroups
      : [params.dZoneGroups];
    params.dZoneGroups = params.dZoneGroups.filter((item: any) => item != null);
    queryParams.delivery_zone_group_ids = Array.isArray(params.dZoneGroups)
      ? params.dZoneGroups
      : [params.dZoneGroups];
  }

  if (params.dManagers) {
    params.dManagers = Array.isArray(params.dManagers)
      ? params.dManagers
      : [params.dManagers];
    params.dManagers = params.dManagers.filter((item: any) => item != null);
    queryParams.manager_ids = Array.isArray(params.dManagers)
      ? params.dManagers
      : [params.dManagers];
  }

  if (params.deliveryZone) {
    params.deliveryZone = Array.isArray(params.deliveryZone)
      ? params.deliveryZone
      : [params.deliveryZone];
    params.deliveryZone = params.deliveryZone.filter(
      (item: any) => item != null,
    );
    queryParams.delivery_zone_ids = Array.isArray(params.deliveryZone)
      ? params.deliveryZone
      : [params.deliveryZone];
  }

  if (params.clients && params.clients.length > 0) {
    params.clients = Array.isArray(params.clients)
      ? params.clients
      : [params.clients];
    params.clients = params.clients.filter((item: any) => item != null);
    queryParams.client_ids = Array.isArray(params.clients)
      ? params.clients
      : [params.clients];
  }

  if (params.freeProductTypes) {
    params.freeProductTypes = Array.isArray(params.freeProductTypes)
      ? params.freeProductTypes
      : [params.freeProductTypes];
    params.freeProductTypes = params.freeProductTypes.filter(
      (item: any) => item != null,
    );
    queryParams.free_product_type_ids = Array.isArray(params.freeProductTypes)
      ? params.freeProductTypes
      : [params.freeProductTypes];
  }

  if (params.sortField !== null) {
    queryParams.order_by = params.sortField || "expected_delivery_date";
    queryParams.order_type = params.sortDirection || "asc";
  }

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";

export const actions: ActionTree<DealReportsState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("reports.deals")
            .action(buildFilter(params)),
        ]).then((result: ServerResult) => {
          commit(
            "SET_REPORT_DATA",
            result.getOperation(REPORT_DATA).getContent(),
          );
          commit("LOADED_SUCCESS");

          resolve(true);
        });
      } else {
        resolve(true);
      }
    });
  },

  exportDataAsXLSX({ rootState }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("reports.deals.export")
          .action({ ...buildFilter(params), format: "xlsx" }),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve(true);
      });
    });
  },

  exportDataAsPDF({ rootState }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("reports.deals.export")
          .action({ ...buildFilter(params), format: "pdf" }),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.pdf");

        resolve(true);
      });
    });
  },
};

export const mutations: MutationTree<DealReportsState> = {
  START_LOADING: (state) => {
    // state.loaded = false;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, data) {
    state.items = data.deals.documents[0].deals || [];
  },
};

const getters = {
  total: (state: DealReportsState) => {
    const total = {
      weight: 0,
      cost: 0,
      point_address: 0,
      products: 0,
    };

    if (state.items) {
      total.weight = state.items.reduce((curr: any, item: any) => {
        return curr + parseInt(item.weight);
      }, 0);
      total.cost = state.items.reduce((curr: any, item: any) => {
        return curr + parseInt(item.cost);
      }, 0);
      total.point_address = state.items.reduce((curr: any, item: any) => {
        if (!curr.includes(item.id)) {
          curr.push(item.id);
        }
        return curr;
      }, []).length;
      total.products = state.items.reduce((curr: any, item: any) => {
        return curr + 1;
      }, 0);
    }
    return total;
  },
};

export const STORE_KEY = "$_deal_reports";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
